// Minimal, dependency-free cookie/consent state manager.
//
// Legal basis: under UK GDPR + PECR, cookies/localStorage that aren't
// "strictly necessary" for a service the visitor explicitly requested
// need prior consent before they're set. On this site that means:
//   - Calendly's embedded widget (consultation page) sets its own
//     third-party cookies - NOT strictly necessary, needs consent.
//   - Google Analytics (gtag.js) - NOT strictly necessary, needs consent.
//     Implemented via Google's Consent Mode v2: the gtag.js script loads
//     on every page (that's fine), but analytics_storage defaults to
//     "denied" in index.html, so no cookie/storage is written until
//     setConsent({ analytics: true }) below calls gtag('consent','update').
//   - The live chat widget's localStorage (visitor ID + saved contact
//     info) only exists to run the chat feature itself, which the visitor
//     explicitly triggers by opening the widget - treated as strictly
//     necessary, so it isn't gated here. Worth a lawyer's sign-off if you
//     want that assumption bulletproof rather than reasonable-and-common.
//
// This is not legal advice - it's a reasonable, commonly-used implementation
// pattern. Get an actual privacy policy page written (and ideally a quick
// solicitor/DPO check) to go with it.

const CONSENT_KEY = "lolcorp_cookie_consent";
const CONSENT_EVENT = "lolcorp-consent-updated";

export const CONSENT_VERSION = 3;

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasDecided() {
  return getConsent() !== null;
}

export function hasConsent(category) {
  const consent = getConsent();
  if (!consent) return false;
  if (category === "essential") return true;
  return !!consent[category];
}

// Pushes the stored consent decision to gtag's Consent Mode. Called
// whenever consent is set, and also on every page load (from
// CookieConsent's mount effect) since gtag resets to its "denied"
// default on every fresh page load/navigation.
function syncGtagConsent(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
}

export function setConsent({ calendly, analytics }) {
  const value = {
    version: CONSENT_VERSION,
    essential: true,
    calendly: !!calendly,
    analytics: !!analytics,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private browsing etc.) - fail silently,
    // the banner will just reappear next visit.
  }
  syncGtagConsent(value);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  return value;
}

// Call once on app mount - re-applies a previously stored consent decision
// to gtag, since Consent Mode defaults to "denied" on every fresh load.
export function rehydrateGtagConsent() {
  const consent = getConsent();
  if (consent) syncGtagConsent(consent);
}

export function onConsentChange(callback) {
  window.addEventListener(CONSENT_EVENT, (e) => callback(e.detail));
  return () => window.removeEventListener(CONSENT_EVENT, callback);
}
